/**
 * Resolve the API base URL safely across environments.
 *
 * Defense-in-depth against the recurring CORS/auth-drop regression where the
 * Emergent platform sometimes bakes a stale preview hostname into
 * `REACT_APP_BACKEND_URL` during deploy.
 *
 * Architecture rule: frontend and backend are ALWAYS served from the same host
 * via Kubernetes ingress. Cross-origin only happens in local dev (localhost).
 *
 * Rules:
 *   1. Server-side render / no window  → "" (relative)
 *   2. No env URL set                  → "" (relative)
 *   3. env URL host == current host    → "" (relative)
 *   4. env URL is localhost / 127.x.x.x → return as-is (dev workflow)
 *   5. Anything else                   → "" (force same-origin) + warn loudly
 *
 * This means it's safe to leave `REACT_APP_BACKEND_URL` empty, set to the
 * preview URL, set to the production URL, or any other valid same-host URL —
 * the app behaves identically and never tries to call a foreign host.
 *
 * IMPORTANT: All app code MUST import `API_BASE` from this file. Direct usage
 * of `process.env.REACT_APP_BACKEND_URL` anywhere else is forbidden — it
 * bypasses this safety net (especially for raw `fetch()` calls that aren't
 * routed through the axios interceptor).
 */

const FOREIGN_HOST_PATTERNS = [
    /\.preview\.emergentagent\.com$/i,
    /\.emergent\.host$/i,
    /\.emergent\.sh$/i,
    /\.emergentagent\.com$/i,
];

let _warned = false;

function _warnOnce(message, details) {
    if (_warned) return;
    _warned = true;
    if (typeof console !== 'undefined' && console.warn) {
        // eslint-disable-next-line no-console
        console.warn(`[apiBase] ${message}`, details);
    }
}

export function getApiBase() {
    if (typeof window === 'undefined') return '';
    const raw = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '').trim();
    if (!raw) return '';
    try {
        const u = new URL(raw);

        // Same host → already same-origin, just use relative paths
        if (u.host === window.location.host) return '';

        // Local dev (localhost / 127.0.0.1 / [::1]) → return as-is for CRA dev workflow
        if (/^(localhost|127\.0\.0\.1|\[::1\])$/i.test(u.hostname)) return raw;

        // Anything else: force same-origin + loud warning so the regression is visible.
        // This catches the classic "preview URL baked into production build" bug.
        const isForeignPreview = FOREIGN_HOST_PATTERNS.some((re) => re.test(u.hostname));
        _warnOnce(
            isForeignPreview
                ? 'REACT_APP_BACKEND_URL points to a foreign Emergent preview host. Forcing same-origin /api/...'
                : 'REACT_APP_BACKEND_URL points to a different host than the current page. Forcing same-origin /api/...',
            {
                env_value: raw,
                env_host: u.host,
                window_host: window.location.host,
                advice: 'Set REACT_APP_BACKEND_URL= (empty) in frontend/.env so all API calls are relative same-origin /api/...',
            },
        );
        return '';
    } catch {
        _warnOnce('REACT_APP_BACKEND_URL is not a valid URL — falling back to same-origin', {
            env_value: raw,
        });
        return '';
    }
}

export const API_BASE = getApiBase();
